import { Controller } from "@hotwired/stimulus"
import * as TapPay from "../services/tappay"

// 付款方式 UI 設定
const PAYMENT_METHOD_CONFIG = {
  [TapPay.PAYMENT_METHODS.CREDIT_CARD]: {
    target: "creditCardForm",
    requiresValidation: true
  },
  [TapPay.PAYMENT_METHODS.LINE_PAY]: {
    target: "linePayInfo",
    requiresValidation: false
  },
  [TapPay.PAYMENT_METHODS.JKO_PAY]: {
    target: "jkoPayInfo",
    requiresValidation: false
  }
}

// Prime 取得方式映射
const PRIME_GETTERS = {
  [TapPay.PAYMENT_METHODS.CREDIT_CARD]: () => TapPay.CreditCard.getPrime(),
  [TapPay.PAYMENT_METHODS.LINE_PAY]: () => TapPay.LinePay.getPrime(),
  [TapPay.PAYMENT_METHODS.JKO_PAY]: () => TapPay.JkoPay.getPrime()
}

// TapPay 欄位狀態：0=正確, 1=尚未輸入, 2=資料不完整, 3=格式錯誤
const CARD_FIELD_ERRORS = {
  number: { 1: "請輸入卡號", 2: "卡號資料不完整", 3: "卡號格式錯誤" },
  expiry: { 1: "請輸入有效期限", 2: "有效期限資料不完整", 3: "有效期限格式錯誤" },
  ccv: { 1: "請輸入安全碼", 2: "安全碼資料不完整", 3: "安全碼格式錯誤" }
}

function capitalizeFirst(str) {
  return str.charAt(0).toUpperCase() + str.slice(1)
}

export default class extends Controller {
  static targets = [
    "errorMessage",
    "inAppBrowserWarning", "copyPaymentLinkButton",
    "creditCardForm", "linePayInfo", "jkoPayInfo",
    "primeInput",
    "analyticsClientIdInput", "analyticsSessionIdInput",
    "homeDeliveryFields", "convenienceStoreFields",
    "shippingMethodInput", "storeTypeSelect",
    "storeIdInput", "storeNameInput", "storePhoneInput", "storeAddressInput",
    "selectedStoreDisplay", "selectedStoreName", "selectedStoreAddress", "storeSelectError",
    "convenienceStoreMapConfig",
    "cardNumberField", "cardNumberError",
    "cardExpiryField", "cardExpiryError",
    "cardCcvField", "cardCcvError"
  ]
  static values = {
    appId: Number,
    appKey: String,
    environment: String,
    gaMeasurementId: String
  }

  connect() {
    this.inAppBrowser = this.isPaymentHostileInAppBrowser()
    this.creditCardValid = false
    this.cardStatus = {}
    this.sdkReady = this.initializeSDK()
    this.handleStoreMapMessage = this.handleStoreMapMessage.bind(this)
    window.addEventListener("message", this.handleStoreMapMessage)
    this.showInAppBrowserWarning()
    this.showCurrentPaymentSection()
    this.toggleShippingMethod()
    this.scrollToFirstError()
  }

  disconnect() {
    window.removeEventListener("message", this.handleStoreMapMessage)
  }

  async initializeSDK() {
    try {
      await TapPay.loadSDK()
      TapPay.setupSDK(this.appIdValue, this.appKeyValue, this.environmentValue)
      TapPay.CreditCard.setup()
      TapPay.CreditCard.onUpdate((update) => {
        this.creditCardValid = update.canGetPrime
        this.cardStatus = update.status
      })
    } catch (error) {
      this.showError(error.message)
    }
  }

  // 根據目前選擇的付款方式顯示對應區塊
  showCurrentPaymentSection() {
    const method = this.selectedMethod
    if (!method) return

    const config = PAYMENT_METHOD_CONFIG[method]
    if (!config) return

    this.hideAllForms()
    this.showTarget(config.target)
  }

  isPaymentHostileInAppBrowser() {
    const ua = navigator.userAgent || ""
    return /FBAN|FBAV|FBIOS|FB_IAB|FB4A|Instagram|Line/i.test(ua)
  }

  showInAppBrowserWarning() {
    if (!this.inAppBrowser || !this.hasInAppBrowserWarningTarget) return

    this.inAppBrowserWarningTarget.classList.remove("hidden")
  }

  async copyPaymentLink() {
    const url = window.location.href

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url)
      } else {
        this.copyWithFallback(url)
      }

      this.setCopyPaymentLinkLabel("已複製付款連結")
    } catch (_error) {
      this.copyWithFallback(url)
      this.setCopyPaymentLinkLabel("已複製付款連結")
    }
  }

  copyWithFallback(value) {
    const input = document.createElement("textarea")
    input.value = value
    input.setAttribute("readonly", "")
    input.style.position = "fixed"
    input.style.opacity = "0"
    document.body.appendChild(input)
    input.select()
    document.execCommand("copy")
    input.remove()
  }

  setCopyPaymentLinkLabel(label) {
    if (!this.hasCopyPaymentLinkButtonTarget) return

    this.copyPaymentLinkButtonTarget.textContent = label
    window.setTimeout(() => {
      if (this.hasCopyPaymentLinkButtonTarget) {
        this.copyPaymentLinkButtonTarget.textContent = "複製付款連結"
      }
    }, 2500)
  }

  // 支援 <select> 及 radio button 兩種選擇方式
  get selectedMethod() {
    const select = this.element.querySelector("select[name*='payment_method']")
    if (select) return select.value

    const radio = this.element.querySelector("input[name*='payment_method']:checked")
    return radio?.value
  }

  selectPaymentMethod(event) {
    this.hideAllForms()
    this.hideError()

    const config = PAYMENT_METHOD_CONFIG[event.target.value]
    if (config) this.showTarget(config.target)
  }

  toggleShippingMethod() {
    const method = this.selectedShippingMethod
    const convenienceStoreSelected = method === "convenience_store"

    if (this.hasHomeDeliveryFieldsTarget) {
      this.homeDeliveryFieldsTarget.classList.toggle("hidden", convenienceStoreSelected)
      this.setSectionFieldsDisabled(this.homeDeliveryFieldsTarget, convenienceStoreSelected)
    }

    if (this.hasConvenienceStoreFieldsTarget) {
      this.convenienceStoreFieldsTarget.classList.toggle("hidden", !convenienceStoreSelected)
      this.setSectionFieldsDisabled(this.convenienceStoreFieldsTarget, !convenienceStoreSelected)
    }

    this.element.dispatchEvent(new CustomEvent("shipping-method-changed", {
      bubbles: true,
      detail: { method }
    }))
  }

  get selectedShippingMethod() {
    const input = this.shippingMethodInputTargets[0]
    if (!input) return "home_delivery"
    if (input.type === "radio") {
      return this.shippingMethodInputTargets.find(field => field.checked)?.value || "home_delivery"
    }

    return input.value || "home_delivery"
  }

  async selectConvenienceStore() {
    this.clearStoreSelectError()
    if (!this.hasConvenienceStoreMapConfigTarget) {
      this.showStoreSelectError("尚未設定超商取貨地圖")
      return
    }

    const config = this.convenienceStoreMapConfigTarget.dataset
    if (!config.actionUrl || !config.customerId || !config.returnUrl) {
      this.showStoreSelectError("尚未設定黑貓契客代號或門市地圖網址")
      return
    }

    const popup = window.open("", "tcat-store-map", "width=1024,height=768")
    if (!popup) {
      this.showStoreSelectError("請允許瀏覽器開啟彈出視窗後再選擇門市")
      return
    }

    this.submitConvenienceStoreMapForm(config)
  }

  submitConvenienceStoreMapForm(config) {
    const form = document.createElement("form")
    form.method = "post"
    form.action = config.actionUrl
    form.target = "tcat-store-map"
    form.classList.add("hidden")

    this.appendHiddenInput(form, "CustomerID", config.customerId)
    this.appendHiddenInput(form, "ReturnUrl", config.returnUrl)
    this.appendHiddenInput(form, "TempVar", "store")

    document.body.appendChild(form)
    form.submit()
    form.remove()
  }

  appendHiddenInput(form, name, value) {
    const input = document.createElement("input")
    input.type = "hidden"
    input.name = name
    input.value = value
    form.appendChild(input)
  }

  handleStoreMapMessage(event) {
    if (event.origin !== window.location.origin) return
    if (event.data?.type !== "tcat:store_selected") return

    this.applySelectedStore(event.data.store || {})
  }

  applySelectedStore(store) {
    this.storeTypeSelectTarget.value = store.store_type || "UNIMART"
    this.storeIdInputTarget.value = store.store_id || ""
    this.storeNameInputTarget.value = store.store_name || ""
    this.storePhoneInputTarget.value = ""
    this.storeAddressInputTarget.value = store.store_address || ""

    this.selectedStoreNameTarget.textContent = this.selectedStoreLabel(store)
    this.selectedStoreAddressTarget.textContent = store.store_address || ""
    this.selectedStoreDisplayTarget.classList.remove("hidden")
    this.storeTypeSelectTarget.classList.remove("is-invalid")
    this.clearStoreSelectError()
  }

  selectedStoreLabel(store) {
    const name = store.store_name || ""
    const id = store.store_id || ""
    if (!name) return id
    return `7-11${name}`
  }

  setSectionFieldsDisabled(section, disabled) {
    section.querySelectorAll("input, select, textarea").forEach(field => {
      field.disabled = disabled
    })
  }

  showStoreSelectError(message) {
    if (!this.hasStoreSelectErrorTarget) return
    this.storeSelectErrorTarget.textContent = message
    this.storeSelectErrorTarget.classList.remove("hidden")
  }

  clearStoreSelectError() {
    if (!this.hasStoreSelectErrorTarget) return
    this.storeSelectErrorTarget.textContent = ""
    this.storeSelectErrorTarget.classList.add("hidden")
  }

  hideAllForms() {
    Object.values(PAYMENT_METHOD_CONFIG).forEach(({ target }) => {
      const hasKey = `has${capitalizeFirst(target)}Target`
      if (this[hasKey]) {
        this[`${target}Target`].classList.add("hidden")
      }
    })
  }

  showTarget(targetName) {
    const hasKey = `has${capitalizeFirst(targetName)}Target`
    if (this[hasKey]) {
      this[`${targetName}Target`].classList.remove("hidden")
    }
  }

  // 攔截表單 submit 事件
  async submit(event) {
    event.preventDefault()

    // in-app browser 只提示不阻擋：3D 驗證導回網址已帶 receipt token，
    // 就算導回時 session 不在也看得到訂單結果。
    const method = this.selectedMethod
    if (!method) {
      this.showError("請選擇付款方式")
      return
    }

    // 同時驗證表單必填欄位與信用卡欄位，全部錯誤一次顯示
    const cardInvalid = method === TapPay.PAYMENT_METHODS.CREDIT_CARD && !this.creditCardValid
    const formValid = this.validateFormFields()
    const shippingValid = this.validateShippingSelection()
    const naturalPersonValid = this.validateNaturalPersonConfirmation()

    if (method === TapPay.PAYMENT_METHODS.CREDIT_CARD) this.showCardFieldErrors()
    if (cardInvalid || !formValid || !shippingValid || !naturalPersonValid) {
      this.scrollToFirstError()
      return
    }

    const submitBtn = this.element.querySelector("[type=submit]")
    const originalLabel = submitBtn?.value

    try {
      this.setSubmitLoading(submitBtn)
      await this.sdkReady
      const prime = await this.getPrimeByMethod(method)
      this.primeInputTarget.value = prime
      await this.captureGa4Identifiers()
      // 直接呼叫 form.submit() 不會再觸發 submit 事件，避免無限迴圈
      this.element.submit()
    } catch (error) {
      this.showError(error.message)
      this.restoreSubmit(submitBtn, originalLabel)
    }
  }

  setSubmitLoading(btn) {
    if (!btn) return
    btn.disabled = true
    btn.value = "處理中..."
  }

  restoreSubmit(btn, originalLabel) {
    if (!btn) return
    btn.disabled = false
    btn.value = originalLabel
  }

  // 驗證表單所有 required 輸入欄位，回傳是否全部合法
  validateFormFields() {
    let valid = true
    this.element.querySelectorAll("input[required], select[required]").forEach(field => {
      if (field.disabled) return

      const errorEl = field.parentElement.querySelector("[data-validation-error]")
      if (field.disabled) {
        field.classList.remove("is-invalid")
        if (errorEl) {
          errorEl.textContent = ""
          errorEl.classList.add("hidden")
        }
        return
      }

      if (!field.value.trim()) {
        field.classList.add("is-invalid")
        if (errorEl) {
          errorEl.textContent = field.dataset.errorMessage || "此欄位必填"
          errorEl.classList.remove("hidden")
        }
        valid = false
      } else {
        field.classList.remove("is-invalid")
        if (errorEl) {
          errorEl.textContent = ""
          errorEl.classList.add("hidden")
        }
      }
    })
    return valid
  }

  validateShippingSelection() {
    const method = this.selectedShippingMethod
    if (method !== "convenience_store") return true

    if (this.storeIdInputTarget.value.trim()) {
      this.clearStoreSelectError()
      return true
    }

    this.showStoreSelectError("請選擇取貨門市")
    return false
  }

  // 顯示信用卡逐欄位錯誤（仿表單 error-hint 樣式）
  showCardFieldErrors() {
    const s = this.cardStatus

    this.setCardFieldError(
      this.cardNumberFieldTarget,
      this.cardNumberErrorTarget,
      s.number,
      CARD_FIELD_ERRORS.number
    )
    this.setCardFieldError(
      this.cardExpiryFieldTarget,
      this.cardExpiryErrorTarget,
      s.expiry,
      CARD_FIELD_ERRORS.expiry
    )
    this.setCardFieldError(
      this.cardCcvFieldTarget,
      this.cardCcvErrorTarget,
      s.ccv,
      CARD_FIELD_ERRORS.ccv
    )
  }

  setCardFieldError(fieldEl, errorEl, status, messages) {
    if (status === 0) {
      // 正確：清除錯誤
      fieldEl.classList.remove("border-red-500", "is-invalid")
      fieldEl.classList.add("border-gray-300")
      errorEl.textContent = ""
      errorEl.classList.add("hidden")
    } else {
      // 尚未輸入、資料不完整、格式錯誤
      fieldEl.classList.add("border-red-500", "is-invalid")
      fieldEl.classList.remove("border-gray-300")
      errorEl.textContent = messages[status] ?? messages[1]
      errorEl.classList.remove("hidden")
    }
  }

  validateNaturalPersonConfirmation() {
    const invoiceType = this.element.querySelector('input[name="order[invoice_type]"]:checked')?.value
    if (invoiceType !== 'natural_person') return true

    const idInput = this.element.querySelector('input[name="order[natural_person_id]"]')
    const confirmInput = this.element.querySelector('input[name="order[natural_person_id_confirmation]"]')
    if (!idInput || !confirmInput) return true

    const errorEl = confirmInput.closest('.mb-3')?.querySelector('[data-natural-person-error]')

    if (idInput.value !== confirmInput.value) {
      confirmInput.classList.add('is-invalid')
      if (errorEl) errorEl.classList.remove('hidden')
      return false
    } else {
      confirmInput.classList.remove('is-invalid')
      if (errorEl) errorEl.classList.add('hidden')
      return true
    }
  }

  async getPrimeByMethod(method) {
    const getter = PRIME_GETTERS[method]
    if (!getter) throw new Error("不支援的付款方式")
    return getter()
  }

  async captureGa4Identifiers() {
    if (typeof window.gtag !== "function" || !this.gaMeasurementIdValue) return

    let clientId
    let sessionId
    try {
      [clientId, sessionId] = await Promise.all([
        this.getGa4Value("client_id"),
        this.getGa4Value("session_id")
      ])
    } catch (_error) {
      // 分析工具異常不能阻擋付款，後端仍會使用 _ga cookie 或 UUID fallback。
      return
    }

    if (clientId && this.hasAnalyticsClientIdInputTarget) {
      this.analyticsClientIdInputTarget.value = clientId
    }
    if (sessionId && this.hasAnalyticsSessionIdInputTarget) {
      this.analyticsSessionIdInputTarget.value = sessionId
    }
  }

  getGa4Value(field) {
    return new Promise(resolve => {
      let settled = false
      const finish = value => {
        if (settled) return
        settled = true
        resolve(value)
      }

      // 追蹤程式或同意管理工具未回應時，不應阻擋付款流程。
      window.setTimeout(() => finish(null), 500)
      window.gtag("get", this.gaMeasurementIdValue, field, finish)
    })
  }

  scrollToFirstError() {
    const firstInvalid = this.element.querySelector(".is-invalid")
    if (!firstInvalid) return

    firstInvalid.scrollIntoView({ behavior: "smooth", block: "center" })

    // 信用卡欄位是 TapPay iframe 容器（div），需 focus iframe 本身
    const iframe = firstInvalid.querySelector("iframe")
    if (iframe) {
      iframe.focus({ preventScroll: true })
    } else {
      firstInvalid.focus({ preventScroll: true })
    }
  }

  showError(message) {
    this.errorMessageTarget.textContent = message
    this.errorMessageTarget.classList.remove("hidden")
  }

  hideError() {
    this.errorMessageTarget.classList.add("hidden")
  }
}
